import type { CdekTrackingData } from './api/schemas/cdek.schema'
import { firstAcceptedCdekStatus } from './cdek-status-history'

export interface CdekTransitPlan {
  days: number
  plannedDeliveryDate: string
}

function dateOnlyUtc(value: string): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
  if (!match) return null
  const [, year, month, day] = match.map(Number)
  const timestamp = Date.UTC(year, month - 1, day)
  return Number.isNaN(timestamp) ? null : timestamp
}

/**
 * CDEK gives a planned delivery date, not a hand-entered delivery duration.
 * Transit starts at the earliest acceptance the provider actually dated, read
 * through the generated vocabulary rather than a local code list, so the
 * displayed duration and outgoing confirmation stay grounded in CDEK data and
 * do not depend on where that entry sits in the array.
 */
export function getCdekTransitPlan(data?: CdekTrackingData | null): CdekTransitPlan | null {
  const plannedDeliveryDate = data?.planned_delivery_date
  if (!plannedDeliveryDate) return null

  const acceptedAt = firstAcceptedCdekStatus(data?.statuses)?.date_time
  if (!acceptedAt) return null

  const plannedDay = dateOnlyUtc(plannedDeliveryDate)
  const acceptedDay = dateOnlyUtc(acceptedAt)
  if (plannedDay === null || acceptedDay === null) return null

  const days = Math.ceil((plannedDay - acceptedDay) / 86_400_000)
  if (!Number.isSafeInteger(days) || days < 0) return null
  return { days, plannedDeliveryDate }
}

export function getCdekTransitPlanFromDates(
  plannedDeliveryDate?: string,
  acceptedAt?: string,
): CdekTransitPlan | null {
  if (!plannedDeliveryDate || !acceptedAt) return null
  return getCdekTransitPlan({
    planned_delivery_date: plannedDeliveryDate,
    statuses: [{
      code: 'RECEIVED_AT_SHIPMENT_WAREHOUSE',
      name: 'Принят на складе отправителя',
      date_time: acceptedAt,
    }],
  })
}
